import { useEffect, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import GameScreenLayout from "@/components/GameScreenLayout"
import {
  getArenaDetails,
  postArenaEnter,
  postArenaLeave,
  postArenaQueueCancel,
  postArenaQueueJoin,
} from "@/api/arenaApi"
import {
  connectBattleSocket,
  emitBattleEvent,
  emitBattleEventWhenReady,
  onBattleEvent,
} from "@/socket/battleSocket"
import { useAuth } from "@/context/AuthContext"
import {
  normalizeArenaEntered,
  normalizeArenaOnlineUpdated,
  normalizeArenaQueued,
} from "@/utils/arenaNormalize"
import { useArenaMatch } from "@/features/arena/useArenaMatch"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import {
  ArenaCombatPanel,
  ArenaLobbyPanel,
  ArenaResultPanel,
  ArenaSlotsPanel,
} from "@/features/arena/components/ArenaMatchPanels"

export default function ArenaRoomPage() {
  const { arenaId } = useParams<{ arenaId: string }>()
  const [searchParams] = useSearchParams()
  const initialMatchId = searchParams.get("matchId")
  const { token } = useAuth()
  const { passport } = useGuardiansPassport()
  const [arenaName, setArenaName] = useState("Арена")
  const [selectedAbilityId, setSelectedAbilityId] = useState<string | null>(null)
  const [onlineCount, setOnlineCount] = useState(0)
  const [actionBusy, setActionBusy] = useState(false)

  const detailsQ = useQuery({
    queryKey: ["arena", arenaId],
    queryFn: () => getArenaDetails(arenaId!),
    enabled: Boolean(arenaId),
  })

  const {
    screenPhase,
    match,
    queueSize,
    needed,
    busy,
    error,
    availableCards,
    myPlayer,
    inActiveDuel,
    claimSlot,
    selectCard,
    forfeit,
    setQueued,
    setCapacityWait,
    clearQueue,
    resetToLobby,
  } = useArenaMatch({
    arenaId: arenaId!,
    initialMatchId,
  })

  useEffect(() => {
    if (detailsQ.data?.name) setArenaName(detailsQ.data.name)
    if (detailsQ.data?.onlineCount != null) setOnlineCount(detailsQ.data.onlineCount)
  }, [detailsQ.data])

  useEffect(() => {
    if (!arenaId) return
    let cancelled = false
    const cleanups: (() => void)[] = []

    const setup = async () => {
      try {
        await connectBattleSocket(token)
      } catch {
        return
      }
      if (cancelled) return

      emitBattleEvent("arena-enter", { arenaId })
      void postArenaEnter(arenaId).catch(() => {})

      cleanups.push(
        onBattleEvent("arena-entered", (raw) => {
          const entered = normalizeArenaEntered(raw)
          if (!entered || entered.arenaId !== arenaId) return
          setArenaName(entered.arenaName)
          setOnlineCount(entered.onlineCount)
        })
      )
      cleanups.push(
        onBattleEvent("arena-online-updated", (raw) => {
          const upd = normalizeArenaOnlineUpdated(raw)
          if (!upd || upd.arenaId !== arenaId) return
          setOnlineCount(upd.onlineCount)
        })
      )
      cleanups.push(
        onBattleEvent("arena-queued", (raw) => {
          const q = normalizeArenaQueued(raw)
          setQueued(q.queueSize, q.needed)
        })
      )
      cleanups.push(
        onBattleEvent("arena-capacity-wait", () => {
          setCapacityWait()
        })
      )
      cleanups.push(
        onBattleEvent("arena-queue-cancelled", () => {
          clearQueue()
        })
      )
    }

    void setup()

    return () => {
      cancelled = true
      cleanups.forEach((fn) => fn())
      emitBattleEvent("arena-leave")
      void postArenaLeave().catch(() => {})
    }
  }, [arenaId, token, setQueued, setCapacityWait, clearQueue])

  async function joinQueue() {
    if (!arenaId || actionBusy) return
    setActionBusy(true)
    try {
      const ok = await emitBattleEventWhenReady(token, "arena-queue-join")
      if (!ok) await postArenaQueueJoin()
    } finally {
      setActionBusy(false)
    }
  }

  async function cancelQueue() {
    if (actionBusy) return
    setActionBusy(true)
    try {
      emitBattleEvent("arena-queue-cancel")
      await postArenaQueueCancel()
      clearQueue()
    } finally {
      setActionBusy(false)
    }
  }

  const queueMode =
    screenPhase === "queue"
      ? "queue"
      : screenPhase === "capacity_wait"
        ? "capacity"
        : "idle"

  const selectedCardId = match.activeDuel?.selectedCardId ?? null
  const guardianRank =
    passport?.guardianRankInfo?.rank ?? passport?.guardianRank ?? 1

  useEffect(() => {
    setSelectedAbilityId(null)
  }, [match.activeDuel?.duelId])

  return (
    <GameScreenLayout title={arenaName} backTo="/play/arena">
      {error ? <p className="mb-2 text-sm text-red-300">{error}</p> : null}

      {screenPhase === "lobby" || screenPhase === "queue" || screenPhase === "capacity_wait" ? (
        <ArenaLobbyPanel
          onlineCount={onlineCount}
          queueSize={queueSize}
          needed={needed}
          queueMode={queueMode}
          onJoinQueue={() => void joinQueue()}
          onCancelQueue={() => void cancelQueue()}
          busy={actionBusy || busy}
        />
      ) : null}

      {screenPhase === "slots" ? (
        <ArenaSlotsPanel
          team={match.team}
          players={match.players}
          myUserId={match.myUserId}
          slotDeadlineAt={match.slotDeadlineAt}
          busy={busy}
          onClaimSlot={(slot) => void claimSlot(slot)}
          onForfeit={() => void forfeit()}
        />
      ) : null}

      {screenPhase === "combat" ? (
        <ArenaCombatPanel
          duel={match.activeDuel}
          inActiveDuel={inActiveDuel}
          cards={availableCards}
          burnedCardIds={match.burnedCardIds}
          selectedCardId={selectedCardId}
          selectedAbilityId={selectedAbilityId}
          affiliationElement={passport?.affiliationElement}
          guardianRank={guardianRank}
          log={match.log}
          busy={busy}
          onSelectCard={(id, abilityId) => void selectCard(id, abilityId)}
          onSelectAbility={setSelectedAbilityId}
          onForfeit={() => void forfeit()}
        />
      ) : null}

      {screenPhase === "result" && match.finished ? (
        <ArenaResultPanel
          finished={match.finished}
          myTeam={myPlayer?.team ?? match.team}
          onAgain={() => resetToLobby()}
        />
      ) : null}

      {screenPhase !== "lobby" &&
      screenPhase !== "queue" &&
      screenPhase !== "capacity_wait" &&
      screenPhase !== "slots" &&
      screenPhase !== "combat" &&
      screenPhase !== "result" ? (
        <p className="text-sm text-guardians-muted">Фаза: {screenPhase}</p>
      ) : null}
    </GameScreenLayout>
  )
}
